import React, { useState, useEffect, Suspense, useTransition } from 'react';
import { TraceabilityDashboardApi } from 'api/endpoints/TraceabilityDashboard';
import { Spinner, Alert, Row, Col, Card } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import RangeDatePicker from 'screens/common/RageDatePicker';
import StatisticsCard from 'components/dashboards/saas/stats-cards/StatisticsCard';

// Use a single ObservationRenderer with direct import
const ObservationRenderer = React.lazy(() =>
  import('screens/common/ObservationRender')
);

// Error boundary component to catch rendering errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Chart error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert variant="warning">
          Unable to load chart. Please try refreshing.
        </Alert>
      );
    }

    return this.props.children;
  }
}

// Add PropTypes validation for children
ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

// Simplified wrapper for ObservationRenderer to handle errors and loading
const ChartWrapper = ({ dataset, loading }) => {
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!dataset || isEmpty(dataset)) {
    return null;
  }

  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        }
      >
        <ObservationRenderer dataset={dataset} loading={loading} />
      </Suspense>
    </ErrorBoundary>
  );
};

ChartWrapper.propTypes = {
  dataset: PropTypes.object,
  loading: PropTypes.bool
};

function TraceabilityDashboard() {
  // Add useTransition hook
  const [isPending, startTransition] = useTransition();

  // States for all API responses
  const [procurementCardData, setProcurementCardData] = useState(null);
  const [transferCardData, setTransferCardData] = useState(null);
  const [receptionCardData, setReceptionCardData] = useState(null);
  const [pendingTransferCardData, setPendingTransferCardData] = useState(null);
  const [warehousePieChartData, setWarehousePieChartData] = useState(null);
  const [tracebilityCropBarChartData, setTracebilityCropBarChartData] =
    useState(null);
  const [procurementMapData, setProcurementMapData] = useState(null);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Date range state (default to current month)
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Fetch data sequentially
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Sequential API calls
        const { startDate, endDate } = dateRange;

        // Use Promise.allSettled to handle individual API call failures
        const results = await Promise.allSettled([
          // 1. Procurement Card Data
          TraceabilityDashboardApi.procurementCard({ startDate, endDate })
            .then(response => {
              startTransition(() => {
                setProcurementCardData(response.data?.results);
              });
            })
            .catch(err => {
              console.error('Error fetching procurement card data:', err);
              return null;
            }),

          // 2. Transfer Card Data
          TraceabilityDashboardApi.transferCard({ startDate, endDate })
            .then(response => {
              startTransition(() => {
                setTransferCardData(response.data?.results);
              });
            })
            .catch(err => {
              console.error('Error fetching transfer card data:', err);
              return null;
            }),

          // 3. Reception Card Data
          TraceabilityDashboardApi.receptionCard({ startDate, endDate })
            .then(response => {
              startTransition(() => {
                setReceptionCardData(response.data?.results);
              });
            })
            .catch(err => {
              console.error('Error fetching reception card data:', err);
              return null;
            }),

          // 4. Pending Transfer Card Data
          TraceabilityDashboardApi.pendingTransferCard({ startDate, endDate })
            .then(response => {
              startTransition(() => {
                setPendingTransferCardData(response.data?.results);
              });
            })
            .catch(err => {
              console.error('Error fetching pending transfer card data:', err);
              return null;
            }),

          // 5. Warehouse Pie Chart Data
          TraceabilityDashboardApi.warehousePieChart({ startDate, endDate })
            .then(response => {
              startTransition(() => {
                setWarehousePieChartData(response.data.results);
              });
            })
            .catch(err => {
              console.error('Error fetching warehouse pie chart data:', err);
              return null;
            }),

          // 6. Traceability Crop Bar Chart Data
          TraceabilityDashboardApi.tracebilityCropBarChart({
            startDate,
            endDate
          })
            .then(response => {
              startTransition(() => {
                setTracebilityCropBarChartData(response.data.results);
              });
            })
            .catch(err => {
              console.error(
                'Error fetching traceability crop bar chart data:',
                err
              );
              return null;
            }),

          // 7. Procurement Map Data
          TraceabilityDashboardApi.procurementMap({ startDate, endDate })
            .then(response => {
              startTransition(() => {
                setProcurementMapData(response.data.results);
              });
            })
            .catch(err => {
              console.error('Error fetching procurement map data:', err);
              return null;
            })
        ]);

        // Check if all promises were rejected
        if (results.every(result => result.status === 'rejected')) {
          setError(
            'Failed to fetch any dashboard data. Please try again later.'
          );
        }
      } catch (err) {
        console.error('Error in fetch operation:', err);
        setError('An unexpected error occurred. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [dateRange]);

  // Handle date range changes
  const handleDateRangeChange = newDateRange => {
    startTransition(() => {
      setDateRange(newDateRange);
    });
  };

  const cardData = {
    ...procurementCardData,
    ...transferCardData,
    ...receptionCardData,
    ...pendingTransferCardData
  };
  const statsData = Object.keys(cardData).map((key, index) => {
    const item = cardData[key];

    const stat = {
      sign: item.percentage > 0 ? '+' : '',
      value: item.count,
      info: ``,
      title: item.title,
      suffix: '',
      desc: ''
    };

    return (
      <Col key={key} md={6} lg={3}>
        <StatisticsCard
          stat={stat}
          style={{ minWidth: '12rem', height: '100%' }}
          index={index}
        />
      </Col>
    );
  });

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '300px' }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div className="traceability-dashboard">
      <Row className="mb-4 align-items-center">
        <Col md={4}>
          <div className="d-flex justify-content-end">
            <RangeDatePicker
              onDateChange={(start, end) => {
                if (start && end) {
                  const formattedStartDate = start.toISOString().split('T')[0];
                  const formattedEndDate = end.toISOString().split('T')[0];
                  startTransition(() => {
                    setDateRange({
                      startDate: formattedStartDate,
                      endDate: formattedEndDate
                    });
                  });
                }
              }}
              noLabel={true}
            />
          </div>
        </Col>
      </Row>
      {cardData && Object.values(cardData).every(item => item?.count > 0) && (
        <Row className="g-3 mb-4">{statsData}</Row>
      )}

      <Row className="g-3 mb-4">
        <ChartWrapper dataset={warehousePieChartData || {}} loading={false} />
      </Row>

      <Row className="g-3 mb-4">
        <ChartWrapper
          dataset={tracebilityCropBarChartData || {}}
          loading={false}
        />
      </Row>

      <Row className="g-3">
        <ChartWrapper dataset={procurementMapData || {}} loading={false} />
      </Row>
    </div>
  );
}

export default TraceabilityDashboard;
